import trace from '~/lib/trace';
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {View, ScrollView, TextInput, Animated} from 'react-native';
import {ActivityIndicator, Divider} from 'react-native-paper';
import {HeaderBackButton} from '@react-navigation/elements';
import isEmpty from 'lodash/isEmpty';
import isFinite from 'lodash/isFinite';
import isNil from 'lodash/isNil';
import pickBy from 'lodash/pickBy';
import moment from 'moment';

import sy from '~/styles';
import withProtectedScreen from '~/components/hoc/with-protected-screen';
import {
  Text,
  Checkbox,
  RadioButton,
  Pressable,
  Link,
  Grouping,
  Dialog,
  KpiWarning,
} from '~/components/controls';
import Format from '~/lib/format';
import {
  service_request as serviceRequestApi,
  rob as robApi,
  car as carApi,
  fleet as fleetApi,
} from '~/api/private';
import {SERVICE_TYPE_COMPONENTS, SERVICE_TYPE_CODES} from '~/types/services';
import {setAppIsLoading, setAppDoneLoading, showTecrmiDialog} from '~/actions';
import {useDispatch} from '~/lib/hooks';
import {RobCodeActionsheet} from '~/components/shared/Actionsheets';
import {isLessorStatus} from '~/types/rob';
import {STATUS_CODES} from '~/types/statuses';
import {SOURCES} from '~/types/sources';
import {REQUEST_TYPES} from '~/types/request';

import DirectionsCarIcon from '~/images/md-icons/directions_car/materialicons/24px.svg';
import CloseIcon from '~/images/md-icons/close/materialicons/24px.svg';
import SearchIcon from '~/images/md-icons/search/materialicons/24px.svg';
import InfoOutlinedIcon from '~/images/md-icons/info/materialsymbolsoutlined/24px.svg';
import UnfoldMoreIcon from '~/images/md-icons/unfold_more/materialicons/24px.svg';

import HighlightText from './components/HighlightText';
import ServiceComponent from './components/ServiceComponent';
import ChangeRobCodeLink from './components/ChangeRobCodeLink';
import Changes from './components/Changes';
import {doSearch} from './helper';
import PropTypes from 'prop-types';

const hasExistingChildWork = (item, existing) =>
  item.children.some((child) => !!existing.works[child.work_id]);

const getChildWorkIds = (item) => item.children.map((child) => child.work_id);

const toChildWorkPayload = (item, child) => ({
  ...child,
  work_text: `${item.description} ${item.action}`,
});

const AddTasksHeaderTitle = () => <Text>Item toevoegen</Text>;
const AddTasksHeaderBackImage = () => <CloseIcon />;
const createAddTasksHeaderLeft = (onPress) => () =>
  <HeaderBackButton backImage={AddTasksHeaderBackImage} onPress={onPress} />;

const Index = ({navigation, route}) => {
  const request_id = route.params?.request_id;
  const damage_id = route.params?.damage_id;
  const license = route.params?.license;
  const [loading, setLoading] = useState(true);

  const [prefix, setPrefix] = useState('');

  const [datetime, setDatetime] = useState(null);
  const [mileage, setMileage] = useState(null);
  const [car, setCar] = useState(null);
  const [tecrmiEnabled, setTecrmiEnabled] = useState(false);
  const [components, setComponents] = useState([]);
  const [search, setSearch] = useState(false);
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [slaCodes, setSlaCodes] = useState([]);
  const [mostUsedCodes, setMostUsedCodes] = useState([]);
  const [slaKpis, setSlaKpis] = useState({});
  const [customCodes, setCustomCodes] = useState({});
  const [contract, setContract] = useState(null);
  const [rob, setRob] = useState(null);
  const [existing, setExisting] = useState({
    components: [],
    codes: {},
    works: {},
    extras: {},
  });
  const [selected, setSelected] = useState({
    components: [],
    codes: {},
    works: {},
    extras: {},
  });
  const [isFleetCar, setIsFleetCar] = useState(false);
  const [isRetailCar, setIsRetailCar] = useState(false);

  const [searching, setSearching] = useState(false);
  const [highlightSearchText, setHighlightSearchText] = useState('');
  const searchTextRef = useRef(null);

  const [showRobCodeActionsheet, setShowRobCodeActionsheet] = useState(null);
  const [showCancelAddDialog, setShowCancelAddDialog] = useState(null);
  const [showMissingRobCodeInfoDialog, setShowMissingRobCodeInfoDialog] =
    useState(false);

  const [showAllTasks, setShowAllTasks] = useState(false);

  const dispatch = useDispatch();

  const navigateBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else if (damage_id) {
      navigation.replace('Damage', {
        damage_id,
      });
    } else {
      navigation.replace('Request', {
        request_id,
      });
    }
  }, [navigation, request_id, damage_id]);

  const cancelAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (prefix?.length === 0) {
      Animated.timing(cancelAnim, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }).start();
      setSearch(false);
      setHighlightSearchText('');
    }

    if (prefix?.length === 1) {
      Animated.timing(cancelAnim, {
        toValue: 100,
        duration: 250,
        useNativeDriver: true,
      }).start();
      setSearch(true);
    }

    doSearch.cancel();
    doSearch(
      prefix,
      request_id,
      damage_id,
      tecrmiEnabled,
      isFleetCar,
      isRetailCar,
      setResults,
      setSearching,
    );

    setHighlightSearchText(prefix);
  }, [
    prefix,
    request_id,
    damage_id,
    tecrmiEnabled,
    isFleetCar,
    isRetailCar,
    cancelAnim,
  ]);

  const hasChanges = useCallback(() => {
    return (
      !isEmpty(selected.components) ||
      !isEmpty(selected.codes) ||
      !isEmpty(selected.works) ||
      !isEmpty(selected.extras)
    );
  }, [selected]);

  const onClose = useCallback(() => {
    if (
      !isEmpty(selected.components) ||
      !isEmpty(selected.codes) ||
      !isEmpty(selected.works) ||
      !isEmpty(selected.extras)
    ) {
      setShowCancelAddDialog(true);
    } else {
      navigateBack();
    }
  }, [selected, navigateBack]);

  const onAdd = useCallback(async () => {
    if (!hasChanges()) {
      navigateBack();
      return;
    }

    dispatch(setAppIsLoading());

    try {
      const request_components = selected.components.map((name) => {
        const component = components.find((c) => c.component_name === name);
        const componentDetails = component ?? {};

        return {
          ...componentDetails,
          name,
        };
      });

      const tasks = {};
      Object.values(selected.works).forEach((item) => {
        tasks[item.id ?? item.task_id ?? item.work_id] = {
          ...item,
          id: item.id ?? item.task_id,
          description: item.work_text ?? item.description,
          rob_code: customCodes[item.work_id]?.code ?? item.rob_code,
          rob_description:
            customCodes[item.work_id]?.description ?? item.rob_description,
        };
      });

      /// TecRMI tasks have precedence over rob tasks.
      Object.values(selected.codes)
        .filter(
          (item) =>
            item.is_main_task ||
            !Object.values(tasks)
              .map((x) => x.rob_code)
              .includes(item.rob_code),
        )
        .forEach((item) => {
          tasks[item.id ?? item.task_id ?? item.rob_code] = {
            ...item,
            id: item.id ?? item.task_id,
            description: item.item ?? item.description,
            work_id: null,
          };
        });

      Object.values(selected.extras).forEach((item) => {
        const work_text =
          item.work_text ?? `${item.description}${item.qualifier ?? ''}`;

        tasks[item.id ?? item.task_id ?? item.rob_code] = {
          ...item,
          id: item.id ?? item.task_id,
          type: 'MAINTENANCE_EXTRA',
          rob_code: customCodes[item.work_id]?.code ?? item.rob_code,
          rob_description:
            customCodes[item.work_id]?.description ?? item.rob_description,
          description: work_text,
          work_text,
        };
      });

      await serviceRequestApi.add_tasks(request_id ?? damage_id, {
        components: request_components,
        tasks: Object.values(tasks).map((task) => ({
          ...task,
          is_damage: !!damage_id,
        })),
      });

      navigateBack();
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(setAppDoneLoading());
    }
  }, [
    components,
    customCodes,
    dispatch,
    hasChanges,
    navigateBack,
    request_id,
    damage_id,
    selected,
  ]);

  const renderHeaderLeft = useMemo(
    () => createAddTasksHeaderLeft(onClose),
    [onClose],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerLeft: renderHeaderLeft,
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerTitle: AddTasksHeaderTitle,
    });
  }, [navigation, renderHeaderLeft]);

  useEffect(() => {
    const fetch = async () => {
      const {result} = await serviceRequestApi.get(
        request_id ?? damage_id,
        dispatch,
      );
      if (
        isLessorStatus(result[0].rob?.status) ||
        result[0].status === STATUS_CODES.Done ||
        result[0].status === STATUS_CODES.Invoiced ||
        result[0].status === STATUS_CODES.Archived
      ) {
        navigateBack();
        return;
      }

      setDatetime(result[0].datetime);
      setMileage(result[0].mileage);
      setComponents(result[0].components);
      setRob(result[0].rob);
      setTecrmiEnabled(result[0].tecrmi_enabled);

      setExisting({
        components: result[0].components
          .filter((item) =>
            Object.values(SERVICE_TYPE_COMPONENTS).includes(
              item.component_name,
            ),
          )
          .map((item) => item.name),
        codes: result[0].components
          .flatMap((c) => c.tasks ?? [])
          .filter(
            (task) =>
              task.rob_code &&
              !['SLA_TASK_DURATION', 'SLA_TASK_AMOUNT'].includes(task.type),
          )
          .reduce((acc, task) => {
            acc[task.rob_code] = task;
            return acc;
          }, {}),
        works: result[0].components
          .flatMap((c) => c.tasks ?? [])
          .filter(
            (task) =>
              task.reference !== null && task.type !== 'MAINTENANCE_EXTRA',
          )
          .reduce((acc, task) => {
            acc[task.reference] = {
              ...task,
              work_id: task.reference,
            };
            return acc;
          }, {}),
        extras: result[0].components
          .flatMap((c) => c.tasks ?? [])
          .filter((task) => task.type === 'MAINTENANCE_EXTRA')
          .reduce((acc, task) => {
            acc[Number.parseInt(task.reference, 10)] = {
              ...task,
              work_id: Number.parseInt(task.reference, 10),
              task_id: task.id,
            };

            return acc;
          }, {}),
      });

      let sla_codes;
      if (damage_id) {
        ({result: sla_codes} = await serviceRequestApi.damage_rob_codes(
          damage_id,
        ));
      } else {
        ({result: sla_codes} = await serviceRequestApi.rob_codes(request_id));
      }
      setSlaCodes(sla_codes.filter((item) => isFinite(item.amount)));

      const {result: sla} =
        (await serviceRequestApi.sla(request_id ?? damage_id)) ?? {};
      setSlaKpis(sla?.kpis ?? {});

      let contract;

      if (result[0].request_type === REQUEST_TYPES.SERVICE_REQUEST) {
        setIsRetailCar(true);
      } else if (result[0].request_type === REQUEST_TYPES.FLEET_REQUEST) {
        const {result: fleet_contract} = await fleetApi.contract(
          result[0].fleet_contract_id,
        );

        contract = fleet_contract.contract;
        setIsFleetCar(true);
      } else {
        const {result: most_used_codes} =
          await serviceRequestApi.most_used_rob_codes(request_id ?? damage_id);
        setMostUsedCodes(most_used_codes);

        ({result: contract} = await robApi.contract(
          result[0].license,
          request_id ?? damage_id,
        ));
      }
      setContract(contract);

      if (contract?.permissions.tecrmi) {
        // TecRMI costs exemption.
        setTecrmiEnabled(true);
      }

      const car = await carApi.fetch(result[0].license);
      setCar(car);

      setLoading(false);
    };

    setLoading(true);
    fetch().catch(trace);
  }, [request_id, damage_id, navigation, dispatch, navigateBack]);

  useEffect(() => {
    if (loading) {
      dispatch(setAppIsLoading());
    } else {
      dispatch(setAppDoneLoading());
    }
  }, [loading, dispatch]);

  useEffect(() => {
    const fetch = async () => {
      if (
        [...selected.components, ...existing.components].includes(
          SERVICE_TYPE_COMPONENTS[SERVICE_TYPE_CODES.Maintenance],
        )
      ) {
        if (suggestions === null) {
          const {result} = await serviceRequestApi.suggestions(request_id);

          setSuggestions(result ?? []);
        }
      } else {
        setSuggestions(null);
      }
    };
    fetch().catch(trace);
  }, [request_id, selected, existing, suggestions]);

  const onCheckWork = (item, exclusive_set) => {
    let changes = {
      [item.work_id]: selected.works[item.work_id]
        ? null
        : {
            ...item,
            ...(item.sla_task ? item.sla_task : {}),
          },
    };

    if (exclusive_set) {
      for (const work_id of exclusive_set) {
        if (work_id !== item.work_id) {
          changes[work_id] = null;
        }
      }
    }

    setSelected({
      ...selected,
      works: pickBy({
        ...selected.works,
        ...changes,
      }),
    });
  };

  const onCheck = (task) => {
    setSelected({
      ...selected,
      codes: pickBy({
        ...selected.codes,
        [task.rob_code]: selected.codes[task.rob_code] ? null : task,
      }),
    });
  };

  const getSearchGroupHeader = (search_source) => {
    if (search_source === SOURCES.Rob) {
      return 'ROB-Net activiteiten';
    }

    if (search_source === SOURCES.TecRMI) {
      return 'TecRMI reparatie';
    }

    return search_source;
  };

  const onCheckComponent = (name, type) => {
    let components = selected.components;

    if (type === SERVICE_TYPE_CODES.Maintenance) {
      setTecrmiEnabled(true);

      if (
        ![...components, ...existing.components].includes(
          SERVICE_TYPE_COMPONENTS[SERVICE_TYPE_CODES.Inspection],
        ) &&
        contract?.vehicle?.maintenance?.nextPeriodicCheckExtended &&
        moment().isAfter(
          moment(contract?.vehicle?.maintenance?.nextPeriodicCheckExtended),
          'date',
        )
      ) {
        components = [
          ...components,
          SERVICE_TYPE_COMPONENTS[SERVICE_TYPE_CODES.Inspection],
        ];
      }
    }

    if (components.includes(name)) {
      components = components.filter((item) => item !== name);
    } else {
      components = [...components, name];
    }

    setSelected({
      ...selected,
      components,
    });
  };

  return (
    <View style={sy.mainView}>
      {!loading && (
        <>
          <View style={[sy['flex-row'], sy['items-center'], sy['p-4']]}>
            <View
              style={[
                sy['flex-row'],
                sy['flex-1'],
                sy['p-2'],
                sy['items-center'],
                {
                  borderRadius: 10,
                  backgroundColor: '#EBEBEB',
                },
              ]}>
              <SearchIcon
                fill={
                  searchTextRef.current?.value?.length ? '#4A4A49' : '#828282'
                }
                width={18}
                height={18}
              />
              <TextInput
                ref={(ref) => {
                  searchTextRef.current = ref;
                }}
                style={[
                  sy.regular,
                  sy['flex-1'],
                  {
                    color:
                      searchTextRef.current?.value?.length > 0
                        ? '#4A4A49'
                        : '#828282',
                    marginLeft: 16,
                  },
                ]}
                placeholder={'Zoeken'}
                onChangeText={(value) => setPrefix(value)}
              />
            </View>
            <Animated.View
              style={[
                sy['mt-2'],
                {
                  height: 29,
                  paddingLeft: searchTextRef.current?.value?.length ? 8 : 0,
                  maxWidth: cancelAnim,
                  justifyContent: 'center',
                },
              ]}>
              <Pressable
                onPress={() => {
                  setPrefix('');
                  setResults([]);
                  searchTextRef.current.clear();
                }}>
                <Text
                  style={[
                    sy.regular,
                    {
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    },
                  ]}>
                  Annuleren
                </Text>
              </Pressable>
            </Animated.View>
          </View>

          <ScrollView style={sy['bg-gray95']}>
            {!search && (
              <>
                {request_id && (
                  <Grouping header="Aanvraag" initial={{expand: true}}>
                    {[
                      SERVICE_TYPE_CODES.Maintenance,
                      SERVICE_TYPE_CODES.Inspection,
                      SERVICE_TYPE_CODES.Tires,
                    ].map((type) => (
                      <View key={type}>
                        <ServiceComponent
                          request_id={request_id}
                          type={type}
                          checked={
                            selected.components.includes(
                              SERVICE_TYPE_COMPONENTS[type],
                            ) ||
                            existing.components.includes(
                              SERVICE_TYPE_COMPONENTS[type],
                            )
                          }
                          datetime={datetime}
                          is_retail_car={isRetailCar}
                          contract={contract}
                          car={car}
                          mileage={mileage}
                          tecrmi_enabled={tecrmiEnabled}
                          components={components}
                          rob={rob}
                          onCheck={onCheckComponent}
                          onRefresh={async () => {
                            const {result} = await serviceRequestApi.get(
                              request_id,
                              dispatch,
                            );

                            setTecrmiEnabled(result[0].tecrmi_enabled);
                          }}
                        />
                      </View>
                    ))}
                  </Grouping>
                )}

                {suggestions?.length > 0 && (
                  <Grouping
                    header="Extra services bij onderhoud"
                    initial={{expand: true}}>
                    <View
                      style={[sy['flex-row'], sy['items-center'], sy['p-4']]}>
                      <View style={[sy['pr-4']]}>
                        <DirectionsCarIcon />
                      </View>
                      <View style={[sy['flex-1'], sy['flex-row']]}>
                        {isFinite(mileage) && (
                          <Text style={[sy.mediumBold]}>
                            {Format.number(mileage, 0, true)}
                            &nbsp;km&nbsp;&middot;&nbsp;
                          </Text>
                        )}
                        <Text style={[sy.mediumBold]}>
                          {Format.number(
                            moment().diff(car.entry_date, 'months') / 12,
                            1,
                            true,
                            {
                              minimumFractionDigits: 0,
                            },
                          )}
                          &nbsp;jaar
                        </Text>
                      </View>
                    </View>
                    <Divider style={sy['bg-gray95']} />
                    {suggestions
                      .filter(
                        (item) =>
                          item.is_maintenance_extra &&
                          (showAllTasks ||
                            item.children.every(
                              (child) =>
                                !slaKpis?.[
                                  customCodes?.[child.work_id]?.code ??
                                    child.rob_code
                                ] && !child.kpi,
                            )),
                      )
                      .map((item) =>
                        item.children.map((child) => {
                          const selectable =
                            isFleetCar || isRetailCar || child.rob_code;

                          return (
                            <React.Fragment key={child.work_id}>
                              <View
                                key={item.id}
                                style={[sy['p-4'], sy['flex-row']]}>
                                {selectable ? (
                                  <RadioButton
                                    checked={
                                      !!existing.extras[child.work_id] ||
                                      !!selected.extras[child.work_id]
                                    }
                                    disabled={item.children.some(
                                      (x) => !!existing.extras[x.work_id],
                                    )}
                                    onPress={() => {
                                      const isSelected =
                                        !!selected.extras[child.work_id];
                                      let changes = {
                                        [child.work_id]: isSelected
                                          ? null
                                          : {
                                              ...child,
                                              description: item.description,
                                            },
                                      };

                                      for (const work of item.children) {
                                        if (work.work_id !== child.work_id) {
                                          changes[work.work_id] = null;
                                        }
                                      }

                                      setSelected({
                                        ...selected,
                                        extras: pickBy({
                                          ...selected.extras,
                                          ...changes,
                                        }),
                                      });
                                    }}
                                  />
                                ) : (
                                  <Pressable
                                    onPress={() =>
                                      setShowMissingRobCodeInfoDialog(true)
                                    }>
                                    <InfoOutlinedIcon fill="#FF6C00" />
                                  </Pressable>
                                )}
                                <View
                                  style={[
                                    sy['flex-1'],
                                    sy['pl-4'],
                                    selectable ? null : sy['view-disabled'],
                                  ]}>
                                  <Text>
                                    {item.description},&nbsp;{child.qualifier}
                                  </Text>
                                  {!isFleetCar &&
                                    !isRetailCar &&
                                    child.rob_code && (
                                      <Text
                                        style={[
                                          sy.small,
                                          sy['text-lightgray'],
                                        ]}>
                                        {child.rob_code}&nbsp;&middot;&nbsp;
                                        {child.rob_description}
                                      </Text>
                                    )}
                                </View>
                                <View
                                  style={[
                                    selectable ? null : sy['view-disabled'],
                                  ]}>
                                  <Text>
                                    {Format.number(child.work_time, 2)}&nbsp;u
                                  </Text>
                                  <View style={[sy['flex-1']]}>
                                    <KpiWarning
                                      style={[
                                        sy['flex-1'],
                                        sy['items-end'],
                                        sy['justify-end'],
                                      ]}
                                      kpi={
                                        slaKpis?.[
                                          customCodes?.[child.work_id]?.code ??
                                            child.rob_code
                                        ] ?? child.kpi
                                      }
                                    />
                                  </View>
                                </View>
                              </View>
                              <Divider style={sy['bg-gray95']} />
                            </React.Fragment>
                          );
                        }),
                      )}
                    {suggestions
                      .filter(
                        (item) =>
                          !item.is_maintenance_extra &&
                          (showAllTasks ||
                            item.children.every(
                              (child) =>
                                !slaKpis?.[
                                  customCodes?.[child.work_id]?.code ??
                                    child.rob_code
                                ] && !child.kpi,
                            )),
                      )
                      .map((item) => {
                        const selectable =
                          isFleetCar ||
                          isRetailCar ||
                          item.children.every((child) => !!child.rob_code);
                        return (
                          <View
                            key={item.id}
                            style={[
                              sy['border-b'],
                              sy['border-lightgray'],
                              sy['py-4'],
                            ]}>
                            <View style={[sy['flex-row']]}>
                              {selectable ? (
                                <Checkbox
                                  containerStyle={[sy['px-4']]}
                                  style={[sy['justify-start']]}
                                  checked={item.children.some(
                                    (child) =>
                                      !!existing.works[child.work_id] ||
                                      !!selected.works[child.work_id],
                                  )}
                                  disabled={item.children.some(
                                    (child) => !!existing.works[child.work_id],
                                  )}
                                  disabledTimeout={0}
                                  onPress={() =>
                                    onCheckWork(
                                      {
                                        ...(item.children.find(
                                          (child) =>
                                            !!selected.works[child.work_id],
                                        ) ?? item.children[0]),
                                        work_text: `${item.description} ${
                                          item.action ?? ''
                                        }`.trim(),
                                      },
                                      item.children.map((item) => item.work_id),
                                    )
                                  }
                                />
                              ) : (
                                <Pressable
                                  style={[sy['px-4']]}
                                  onPress={() =>
                                    setShowMissingRobCodeInfoDialog(true)
                                  }>
                                  <InfoOutlinedIcon fill="#FF6C00" />
                                </Pressable>
                              )}
                              <View
                                style={[
                                  sy['flex-1'],
                                  selectable ? null : sy['view-disabled'],
                                ]}>
                                <Text>
                                  {item.description}
                                  {!!item.action &&
                                    item.action !== 'uitvoeren' && (
                                      <Text>&nbsp;{item.action}</Text>
                                    )}
                                </Text>
                                {!item.add_text &&
                                  item.children.length === 1 &&
                                  item.children[0].qualifier !==
                                    'Standaard' && (
                                    <Text
                                      style={[sy.small, sy['text-lightgray']]}>
                                      {item.children[0].qualifier}
                                    </Text>
                                  )}
                                {!!item.add_text && (
                                  <Text
                                    style={[sy.small, sy['text-lightgray']]}>
                                    {item.add_text}
                                  </Text>
                                )}
                                {!isFleetCar &&
                                  !isRetailCar &&
                                  item.children.length === 1 && (
                                    <ChangeRobCodeLink
                                      rob={rob}
                                      task_id={item.children[0].task_id}
                                      rob_code={item.children[0].rob_code}
                                      rob_description={
                                        item.children[0].rob_description
                                      }
                                      work_id={item.children[0].work_id}
                                      custom_codes={customCodes}
                                      onPress={(work_id, rob_code) => {
                                        setShowRobCodeActionsheet({
                                          rob_code,
                                          work_id,
                                        });
                                      }}
                                    />
                                  )}
                              </View>
                              <View
                                style={[
                                  sy['px-4'],
                                  selectable ? null : sy['view-disabled'],
                                ]}>
                                {item.children.length === 1 && (
                                  <>
                                    <Text>
                                      {Format.number(
                                        item.children[0].work_time,
                                        2,
                                      )}
                                      &nbsp;u
                                    </Text>
                                    <View style={[sy['flex-1']]}>
                                      <KpiWarning
                                        style={[
                                          sy['flex-1'],
                                          sy['items-end'],
                                          sy['justify-end'],
                                        ]}
                                        kpi={
                                          slaKpis?.[
                                            customCodes?.[
                                              item.children[0].work_id
                                            ]?.code ?? item.children[0].rob_code
                                          ] ?? item.children[0].kpi
                                        }
                                      />
                                    </View>
                                  </>
                                )}
                              </View>
                            </View>
                            {item.children.length > 1 &&
                              item.children.map((child) => (
                                <View
                                  key={child.work_id}
                                  style={[
                                    sy['flex-row'],
                                    sy['justify-between'],
                                    sy['p-2'],
                                    sy['pl-8'],
                                    sy['pr-4'],
                                    selectable ? null : sy['view-disabled'],
                                  ]}>
                                  <View style={[sy['flex-row'], sy['flex-1']]}>
                                    <RadioButton
                                      checked={
                                        !!existing.works[child.work_id] ||
                                        !!selected.works[child.work_id]
                                      }
                                      disabled={
                                        !selectable ||
                                        hasExistingChildWork(item, existing)
                                      }
                                      onPress={() => {
                                        onCheckWork(
                                          toChildWorkPayload(item, child),
                                          getChildWorkIds(item),
                                        );
                                      }}
                                    />
                                    <View style={[sy['pl-4'], sy['flex-1']]}>
                                      <Text style={[sy.truncate]}>
                                        {child.qualifier}
                                      </Text>

                                      {!isFleetCar && !isRetailCar && (
                                        <ChangeRobCodeLink
                                          rob={rob}
                                          task_id={child.task_id}
                                          rob_code={child.rob_code}
                                          rob_description={
                                            child.rob_description
                                          }
                                          work_id={child.work_id}
                                          custom_codes={customCodes}
                                          onPress={(work_id, rob_code) => {
                                            setShowRobCodeActionsheet({
                                              rob_code,
                                              work_id,
                                            });
                                          }}
                                        />
                                      )}
                                    </View>
                                  </View>
                                  <View>
                                    <Text>
                                      {Format.number(child.work_time, 2)}&nbsp;u
                                    </Text>
                                    <View style={[sy['flex-1']]}>
                                      <KpiWarning
                                        style={[
                                          sy['flex-1'],
                                          sy['items-end'],
                                          sy['justify-end'],
                                        ]}
                                        kpi={
                                          slaKpis?.[
                                            customCodes?.[child.work_id]
                                              ?.code ?? child.rob_code
                                          ] ?? child.kpi
                                        }
                                      />
                                    </View>
                                  </View>
                                </View>
                              ))}
                          </View>
                        );
                      })}
                    {!showAllTasks &&
                      suggestions.some((item) =>
                        item.children.some(
                          (child) =>
                            slaKpis?.[
                              customCodes?.[child.work_id]?.code ??
                                child.rob_code
                            ] ?? child.kpi,
                        ),
                      ) && (
                        <Pressable
                          style={[sy['p-4'], sy['flex-row']]}
                          onPress={() => setShowAllTasks(true)}>
                          <View style={[sy['pr-8']]}>
                            <UnfoldMoreIcon fill="#231fda" />
                          </View>
                          <Text>Meer services tonen</Text>
                        </Pressable>
                      )}
                  </Grouping>
                )}

                {mostUsedCodes.length > 0 && (
                  <Grouping
                    header="ROB activiteiten (meest gekozen reparatie)"
                    initial={{expand: true}}>
                    {mostUsedCodes.map((item) => (
                      <View
                        key={item.rob_code}
                        style={[
                          sy['border-b'],
                          sy['border-lightgray'],
                          sy['py-4'],
                        ]}>
                        <View style={[sy['flex-row']]}>
                          <Checkbox
                            containerStyle={[sy['px-4']]}
                            style={[sy['justify-start']]}
                            checked={
                              !!existing.codes[item.rob_code] ||
                              !!selected.codes[item.rob_code]
                            }
                            disabled={!!existing.codes[item.rob_code]}
                            disabledTimeout={0}
                            onPress={() => onCheck(item)}
                          />
                          <View style={[sy['flex-1']]}>
                            <Text>
                              {item.rob_code}&nbsp;&middot;&nbsp;
                              {item.description}
                            </Text>
                            {item.sla && (
                              <Text style={[sy.small, sy['text-lightgray']]}>
                                SLA&nbsp;&middot;&nbsp;{item.sla?.item}
                              </Text>
                            )}
                          </View>
                          {item.sla && (
                            <View style={[sy['px-4']]}>
                              <Text>{Format.price(item.sla?.amount, 2)}</Text>
                            </View>
                          )}
                        </View>
                      </View>
                    ))}
                  </Grouping>
                )}
              </>
            )}
            {searching && (
              <>
                <Divider style={sy['bg-gray88']} />
                <ActivityIndicator size="large" color="#231fda" />
              </>
            )}
            {search && !searching && (
              <>
                {Object.keys(results).map((search_source, index) => (
                  <React.Fragment key={search_source}>
                    {isNil(results[search_source]) ? (
                      <></>
                    ) : (
                      <Grouping
                        header={getSearchGroupHeader(search_source)}
                        initial={{expand: true}}>
                        {results[search_source].length === 0 && (
                          <View
                            style={[
                              sy['p-4'],
                              sy['items-center'],
                              sy['justify-center'],
                            ]}>
                            <Text>Geen zoekresultaten beschikbaar...</Text>
                            {search_source === SOURCES.TecRMI &&
                              !tecrmiEnabled && (
                                <Link
                                  onPress={async () => {
                                    try {
                                      await new Promise((resolve, reject) => {
                                        dispatch(
                                          showTecrmiDialog({
                                            resolve,
                                            reject,
                                          }),
                                        );
                                      });

                                      await serviceRequestApi.enable_tecrmi(
                                        request_id ?? damage_id,
                                      );
                                      setTecrmiEnabled(true);

                                      doSearch(
                                        prefix,
                                        request_id,
                                        damage_id,
                                        true,
                                        isFleetCar,
                                        isRetailCar,
                                        setResults,
                                        setSearching,
                                      );
                                    } catch {
                                      return;
                                    }
                                  }}
                                  textStyle={{color: '#2907E3'}}>
                                  Service info ophalen
                                </Link>
                              )}
                          </View>
                        )}

                        {results[search_source].map((result, index, arr) => {
                          let checked = false;
                          let disabled = false;
                          let sla_item;
                          switch (result.search_source) {
                            case SOURCES.Rob:
                            case SOURCES.SlaTasks:
                              sla_item = slaCodes.find(
                                (item) => item.rob_code === result.rob_code,
                              );
                              break;
                            default:
                              break;
                          }

                          switch (result.search_source) {
                            case SOURCES.Rob:
                              checked = !!selected.codes[result.rob_code];
                              disabled = !!existing.codes[result.rob_code];
                              sla_item = slaCodes.find(
                                (item) => item.rob_code === result.rob_code,
                              );
                              break;
                            case SOURCES.SlaTasks:
                            case SOURCES.TecRMI:
                            case SOURCES.Make:
                              checked = !!selected.works[result.work_id];
                              disabled = !!existing.works[result.work_id];
                              break;
                            default:
                              break;
                          }

                          return (
                            <React.Fragment
                              key={
                                result.id ?? result.work_id ?? result.rob_code
                              }>
                              <View
                                style={[
                                  sy['flex-row'],
                                  sy['p-4'],
                                  sy['gap-8'],
                                ]}>
                                <Checkbox
                                  style={[
                                    sy['justify-start'],
                                    {height: 'auto', width: 'auto'},
                                  ]}
                                  checked={disabled || checked}
                                  disabled={disabled}
                                  disabledTimeout={0}
                                  onPress={async () => {
                                    switch (result.search_source) {
                                      case SOURCES.Rob:
                                        onCheck(result);
                                        break;
                                      case SOURCES.TecRMI: {
                                        const {suggestion, ...rest} = result;
                                        onCheckWork({
                                          ...rest,
                                          rob_code: suggestion.code,
                                          operation: suggestion.operation,
                                          rob_description:
                                            suggestion.description,
                                          sla_task: suggestion.sla_task,
                                        });
                                        break;
                                      }
                                      case SOURCES.SlaTasks:
                                      case SOURCES.Make:
                                        onCheckWork(result);
                                        break;
                                      default:
                                        break;
                                    }
                                  }}
                                />
                                <View
                                  style={[
                                    sy['flex-row'],
                                    sy['flex-1'],
                                    sy['justify-between'],
                                    sy['gap-4'],
                                  ]}>
                                  <View style={[sy['flex-1']]}>
                                    <View style={{display: 'inline'}}>
                                      {result.search_source === SOURCES.Rob && (
                                        <>
                                          <HighlightText
                                            text={result.rob_code}
                                            highlight={highlightSearchText}
                                          />
                                          <Text>&nbsp;&middot;&nbsp;</Text>
                                        </>
                                      )}
                                      <HighlightText
                                        text={
                                          result.work_text ?? result.description
                                        }
                                        highlight={highlightSearchText}
                                      />
                                    </View>
                                    {[SOURCES.Rob, SOURCES.SlaTasks].includes(
                                      result.search_source,
                                    ) &&
                                      sla_item && (
                                        <Text
                                          style={[
                                            sy.small,
                                            sy['text-lightgray'],
                                          ]}>
                                          SLA&nbsp;&middot;&nbsp;{sla_item.item}
                                        </Text>
                                      )}
                                    {result.search_source ===
                                      SOURCES.TecRMI && (
                                      <>
                                        {result.qualifier &&
                                          result.qualifier !== 'Standaard' && (
                                            <Text
                                              style={[
                                                sy.small,
                                                sy['text-gray'],
                                              ]}>
                                              {result.qualifier}
                                            </Text>
                                          )}
                                        {result.add_text &&
                                          result.add_text !== 'Standaard' && (
                                            <Text
                                              style={[
                                                sy.small,
                                                sy['text-gray'],
                                              ]}>
                                              {result.add_text}
                                            </Text>
                                          )}
                                        {result.suggestion?.sla_task && (
                                          <Text
                                            style={[sy.small, sy['text-gray']]}>
                                            SLA&nbsp;&middot;&nbsp;
                                            {
                                              result.suggestion.sla_task
                                                ?.work_text
                                            }
                                          </Text>
                                        )}
                                        {!isFleetCar &&
                                          !isRetailCar &&
                                          result.suggestion && (
                                            <ChangeRobCodeLink
                                              rob={rob}
                                              task_id={result.task_id}
                                              rob_code={result.suggestion.code}
                                              rob_description={
                                                result.suggestion.description
                                              }
                                              work_id={result.work_id}
                                              custom_codes={customCodes}
                                              onPress={(work_id, rob_code) => {
                                                setShowRobCodeActionsheet({
                                                  rob_code,
                                                  work_id,
                                                });
                                              }}
                                            />
                                          )}
                                      </>
                                    )}
                                  </View>
                                  {result.search_source === SOURCES.Rob && (
                                    <View
                                      style={[
                                        sy['flex-row'],
                                        sy['items-start'],
                                        sy['gap-2'],
                                      ]}>
                                      {sla_item && (
                                        <Text style={sy.smallRegular}>
                                          {Format.price(sla_item.amount, 2)}
                                        </Text>
                                      )}
                                      <KpiWarning
                                        kpi={slaKpis?.[result.rob_code]}
                                      />
                                    </View>
                                  )}
                                  {result.search_source ===
                                    SOURCES.SlaTasks && (
                                    <View>
                                      {(() => {
                                        if (sla_item) {
                                          return (
                                            <Text style={sy.smallRegular}>
                                              {Format.price(sla_item.amount, 2)}
                                            </Text>
                                          );
                                        }

                                        if (
                                          result.type === 'SLA_TASK_DURATION'
                                        ) {
                                          if (!isFinite(result.work_time)) {
                                            return null;
                                          }

                                          return (
                                            <Text>
                                              {Format.number(
                                                result.work_time,
                                                2,
                                              )}
                                              &nbsp;u
                                            </Text>
                                          );
                                        }

                                        return (
                                          <Text>
                                            {Format.price(result.amount)}
                                          </Text>
                                        );
                                      })()}
                                      <View style={[sy['flex-1']]}>
                                        <KpiWarning
                                          style={[
                                            sy['flex-1'],
                                            sy['items-end'],
                                            sy['justify-end'],
                                          ]}
                                          kpi={
                                            slaKpis?.[result.condition?.code]
                                          }
                                        />
                                      </View>
                                    </View>
                                  )}
                                  {result.search_source === SOURCES.TecRMI &&
                                    isFinite(result.work_time) && (
                                      <View>
                                        <Text>
                                          {Format.number(result.work_time, 2)}
                                          &nbsp;u
                                        </Text>
                                        <View style={[sy['flex-1']]}>
                                          <KpiWarning
                                            style={[
                                              sy['flex-1'],
                                              sy['items-end'],
                                              sy['justify-end'],
                                            ]}
                                            kpi={
                                              slaKpis?.[
                                                customCodes[result.work_id]
                                                  ?.code ??
                                                  result.suggestion.code
                                              ]
                                            }
                                          />
                                        </View>
                                      </View>
                                    )}
                                  {result.search_source === SOURCES.Make &&
                                    isFinite(result.work_time) && (
                                      <View>
                                        <Text>
                                          {Format.number(result.work_time, 2)}
                                          &nbsp;u
                                        </Text>
                                        <View style={[sy['flex-1']]}>
                                          <KpiWarning
                                            style={[
                                              sy['flex-1'],
                                              sy['items-end'],
                                              sy['justify-end'],
                                            ]}
                                            kpi={
                                              slaKpis?.[
                                                customCodes[result.work_id]
                                                  ?.code ??
                                                  result.suggestion?.code
                                              ]
                                            }
                                          />
                                        </View>
                                      </View>
                                    )}
                                </View>
                              </View>
                              {index < arr.length - 1 && (
                                <Divider
                                  style={[sy['bg-gray95'], sy['ml-18']]}
                                />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </Grouping>
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
          </ScrollView>
          <Divider style={sy['bg-gray88']} />
          <View style={[sy['p-4'], sy['flex-row'], sy['justify-end']]}>
            <Changes
              selected={selected}
              hasChanges={hasChanges}
              onAdd={onAdd}
            />
          </View>
          {showRobCodeActionsheet && (
            <RobCodeActionsheet
              request_id={request_id}
              license={license}
              codes={[showRobCodeActionsheet.rob_code].filter(Boolean)}
              rob_code={showRobCodeActionsheet.rob_code}
              onDismiss={() => setShowRobCodeActionsheet(null)}
              onChange={(code, description) => {
                setCustomCodes({
                  ...customCodes,
                  [showRobCodeActionsheet.work_id]: {
                    code,
                    description,
                  },
                });
                setShowRobCodeActionsheet(null);
              }}
              options={{maintenance_codes: true}}
            />
          )}
          <Dialog
            visible={showCancelAddDialog}
            title="Selectie niet toevoegen?"
            onDismiss={() => setShowCancelAddDialog(false)}
            buttons={[
              {
                text: 'Annuleren',
                onPress: () => setShowCancelAddDialog(false),
              },
              {
                text: 'Niet toevoegen',
                onPress: navigation.goBack,
              },
            ]}>
            <View style={[sy['py-4']]}>
              <Text>
                Weet je zeker dat je dit venster wil sluiten zonder de selectie
                toe te voegen?
              </Text>
            </View>
          </Dialog>
          <Dialog
            visible={showMissingRobCodeInfoDialog}
            title="Keuze maken onderhoud"
            titleIcon={<InfoOutlinedIcon fill="#FF6C00" />}
            onDismiss={() => setShowMissingRobCodeInfoDialog(false)}>
            <View style={[sy['py-4']]}>
              <Text>
                Deze activiteit komt beschikbaar na het kiezen van een standaard
                service bij onderhoud in de werkopdracht.
              </Text>
            </View>
          </Dialog>
        </>
      )}
    </View>
  );
};
Index.propTypes = {
  navigation: PropTypes.any,
  route: PropTypes.any,
};

export default withProtectedScreen(Index);
